import React from "react";
import embed from "vega-embed";
import { CHART_PALETTE as DEFAULT_CHART_PALETTE, CHART_BACKGROUND, CHART_AXIS_COLOR, CHART_AXIS_LINE_COLOR, CHART_GRID_COLOR, CHART_LEGEND_TEXT_COLOR } from "../theme/charts";
import { IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Image, PhotoCamera, Code } from '@mui/icons-material';
import { ChartLegend, extractLegendData } from './ChartLegend';

export default function ChartRenderer({ spec, onRendered, onViewReady, chartPalette }: { spec: any; onRendered?: () => void; onViewReady?: (viewRef: React.RefObject<any>) => void; chartPalette?: string[] }) {
  // Use provided palette or fall back to default
  const CHART_PALETTE = chartPalette || DEFAULT_CHART_PALETTE;
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const innerRef = React.useRef<HTMLDivElement>(null);
  const viewRef = React.useRef<any>(null);
  const [exportMode, setExportMode] = React.useState(false);
  const [imgUrl, setImgUrl] = React.useState<string | null>(null);
  const [containerWidth, setContainerWidth] = React.useState<number>(0);
  const [legendData, setLegendData] = React.useState<any>(null);
  const [legendOrientation, setLegendOrientation] = React.useState<'vertical' | 'horizontal'>('vertical');

  // Observe width changes of the wrapper; charts often mount before layout assigns width
  React.useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof (window as any).ResizeObserver === 'undefined') return;
    const ro = new (window as any).ResizeObserver((entries: any[]) => {
      for (const entry of entries) {
        const cr = entry.contentRect || entry.target.getBoundingClientRect();
        const w = Math.max(0, Math.floor(cr?.width || 0));
        setContainerWidth((prev: number) => (prev !== w ? w : prev));
      }
    });
    try { ro.observe(el); } catch {}
    // Initialize once
    try {
      const rect = el.getBoundingClientRect();
      const w = Math.max(0, Math.floor(rect?.width || 0));
      setContainerWidth((prev: number) => (prev !== w ? w : prev));
    } catch {}
    return () => { try { ro.disconnect(); } catch {} };
  }, []);

  React.useEffect(() => {
    if (!innerRef.current) return;
    let view: any;
    let cancelled = false;
    (async () => {
      try {
        // Determine available container width; if zero, fall back to a sensible width
        const cw = containerWidth || (wrapperRef.current?.getBoundingClientRect().width || 0);
            const specToUse = (() => {
          try {
            const clone = JSON.parse(JSON.stringify(spec || {}));
            if (!clone || typeof clone !== 'object') return spec;
            if (!clone.autosize) clone.autosize = { type: 'fit', contains: 'padding' };
            if (clone.padding == null) {
              // Very generous padding to prevent clipping of charts and legends
              // Especially important for pie charts with many legend items
              clone.padding = { left: 16, right: 16, top: 32, bottom: 32 } as any;
            }
                // Normalize numeric padding to object form
                try {
                  if (typeof clone.padding === 'number') {
                    const p = Number(clone.padding) || 0;
                    clone.padding = { left: p, right: p, top: p, bottom: p } as any;
                  }
                } catch {}
            // Always use full container width for responsive charts
            clone.width = cw > 0 ? cw : 640;
            // Generous default height to prevent clipping with legends and complex charts
            if (!clone.height) clone.height = 450;
            // Inject a consistent, more vibrant default theme if not provided by the spec
            try {
              clone.config = clone.config || {};
              if (clone.config.background == null) clone.config.background = CHART_BACKGROUND;
              clone.config.range = clone.config.range || {};
              // Force theme palette globally for categorical range to ensure consistent colors across specs
              (clone.config.range as any).category = CHART_PALETTE;
              // Enterprise-grade axes and legends with better readability
              clone.config.axis = {
                ...(clone.config.axis || {}),
                labelColor: clone.config.axis?.labelColor ?? CHART_AXIS_COLOR,
                tickColor: clone.config.axis?.tickColor ?? CHART_AXIS_COLOR,
                domainColor: clone.config.axis?.domainColor ?? CHART_AXIS_LINE_COLOR,
                gridColor: clone.config.axis?.gridColor ?? CHART_GRID_COLOR,
                gridOpacity: clone.config.axis?.gridOpacity ?? 0.5,
                gridDash: clone.config.axis?.gridDash ?? [2, 2],
                labelFontSize: clone.config.axis?.labelFontSize ?? 11,
                titleFontSize: clone.config.axis?.titleFontSize ?? 12,
                titleFontWeight: clone.config.axis?.titleFontWeight ?? 600,
                labelPadding: clone.config.axis?.labelPadding ?? 8,
                titlePadding: clone.config.axis?.titlePadding ?? 12,
                domainWidth: clone.config.axis?.domainWidth ?? 1,
                tickSize: clone.config.axis?.tickSize ?? 5,
                labelLimit: clone.config.axis?.labelLimit ?? 200, // Prevent label truncation
              } as any;
              
              // X-axis specific config for better label handling
              clone.config.axisX = {
                ...(clone.config.axisX || {}),
                labelAngle: clone.config.axisX?.labelAngle ?? -45, // Rotate labels to prevent overlap
                labelAlign: clone.config.axisX?.labelAlign ?? 'right',
                labelLimit: clone.config.axisX?.labelLimit ?? 200,
              } as any;
              clone.config.legend = {
                ...(clone.config.legend || {}),
                labelColor: clone.config.legend?.labelColor ?? CHART_LEGEND_TEXT_COLOR,
                titleColor: clone.config.legend?.titleColor ?? CHART_LEGEND_TEXT_COLOR,
                symbolType: clone.config.legend?.symbolType ?? 'circle',
                symbolSize: clone.config.legend?.symbolSize ?? 100,
                labelFontSize: clone.config.legend?.labelFontSize ?? 11,
                titleFontSize: clone.config.legend?.titleFontSize ?? 12,
                titleFontWeight: clone.config.legend?.titleFontWeight ?? 600,
                padding: clone.config.legend?.padding ?? 8,
                labelLimit: clone.config.legend?.labelLimit ?? 150,
              } as any;
              // Enable enhanced tooltips with better formatting
              clone.config.mark = { 
                ...(clone.config.mark || {}), 
                tooltip: (clone.config.mark as any)?.tooltip ?? { content: 'data' },
                opacity: (clone.config.mark as any)?.opacity ?? 1,
                cursor: (clone.config.mark as any)?.cursor ?? 'pointer',
              } as any;
              clone.config.view = { 
                ...(clone.config.view || {}), 
                stroke: clone.config.view?.stroke ?? 'transparent',
                // Use container width for continuous scales, fallback to reasonable size
                continuousWidth: cw > 0 ? cw : 640,
                continuousHeight: clone.config.view?.continuousHeight ?? 300,
              } as any;
              // Enhanced tooltip styling
              clone.config.title = {
                ...(clone.config.title || {}),
                fontSize: clone.config.title?.fontSize ?? 14,
                fontWeight: clone.config.title?.fontWeight ?? 600,
                color: clone.config.title?.color ?? CHART_LEGEND_TEXT_COLOR,
                anchor: clone.config.title?.anchor ?? 'start',
                offset: clone.config.title?.offset ?? 12,
              } as any;
              // Enforce theme palette for categorical color channels even when a named scheme is provided.
              // Do not override if a custom explicit range is already set.
              const applyColorScale = (unit: any) => {
                if (!unit || typeof unit !== 'object') return;
                const enc = unit.encoding;
                const isNominalOrOrdinal = (ch: any) => {
                  try {
                    const t = (ch?.type || '').toString().toLowerCase();
                    return t === 'nominal' || t === 'ordinal';
                  } catch { return false; }
                };
                const isQuant = (ch: any) => {
                  try { return (ch?.type || '').toString().toLowerCase() === 'quantitative'; } catch { return false; }
                };
                const fix = (ch: any) => {
                  if (!ch) return;
                  const sc = ch.scale || {};
                  const hasRange = sc && Object.prototype.hasOwnProperty.call(sc, 'range');
                  const hasScheme = sc && Object.prototype.hasOwnProperty.call(sc, 'scheme');
                  if (!hasRange && hasScheme) {
                    // Replace named categorical scheme with our palette
                    ch.scale = { ...(sc || {}), range: CHART_PALETTE };
                    try { delete ch.scale.scheme; } catch {}
                  } else if (!hasRange && isNominalOrOrdinal(ch)) {
                    // Ensure a theme range exists for nominal/ordinal channels
                    ch.scale = { ...(sc || {}), range: CHART_PALETTE };
                  }
                };
                if (enc && enc.color) fix(enc.color);
                // Handle conditional encodings array/object
                try {
                  if (enc && Array.isArray(enc.fill)) enc.fill.forEach(fix);
                  else if (enc && enc.fill) fix(enc.fill);
                  if (enc && Array.isArray(enc.stroke)) enc.stroke.forEach(fix);
                  else if (enc && enc.stroke) fix(enc.stroke);
                } catch {}
              };
              const enforceThemeStrict = (unit: any, idx: number) => {
                if (!unit || typeof unit !== 'object') return;
                const enc = unit.encoding || {};
                const coerce = (ch: any) => {
                  if (!ch) return;
                  const t = (ch?.type || '').toString().toLowerCase();
                  if (t === 'quantitative') return; // don't override continuous scales
                  // If explicit range array exists, override with theme
                  if (ch.scale && Array.isArray(ch.scale.range)) {
                    ch.scale = { ...(ch.scale || {}), range: CHART_PALETTE };
                  }
                  // If explicit value color exists, drop it and use mark color from palette
                  if (Object.prototype.hasOwnProperty.call(ch, 'value')) {
                    try { delete ch.value; } catch {}
                  }
                };
                coerce(enc.color);
                if (Array.isArray(enc.fill)) enc.fill.forEach(coerce); else if (enc.fill) coerce(enc.fill);
                if (Array.isArray(enc.stroke)) enc.stroke.forEach(coerce); else if (enc.stroke) coerce(enc.stroke);
                // Also normalize mark-level explicit colors for non-quantitative series-only visuals
                let mark = unit.mark;
                if (typeof mark === 'string') mark = { type: mark };
                if (!mark) mark = {};
                if (mark.color || mark.fill || mark.stroke) {
                  mark.color = CHART_PALETTE[idx % CHART_PALETTE.length];
                  try { delete (mark as any).fill; delete (mark as any).stroke; } catch {}
                  unit.mark = mark;
                }
              };
              const adjustLegendOrientation = (unit: any) => {
                if (!unit || typeof unit !== 'object') return;
                const enc = unit.encoding || {};
                const ensureLegendObject = (ch: any) => {
                  if (!ch) return undefined;
                  if (ch.legend === null || ch.legend === false) return undefined;
                  if (ch.legend === true || ch.legend == null) ch.legend = {};
                  if (typeof ch.legend !== 'object') ch.legend = {};
                  return ch.legend;
                };
                // Prefer gradient legend for quantitative color on the right
                try {
                  const color = enc.color;
                  const colorType = String(color?.type || '').toLowerCase();
                  const legendObj = ensureLegendObject(color);
                  if (legendObj && !('orient' in legendObj)) {
                    const scaleType = String(color?.scale?.type || '').toLowerCase();
                    const isQuant = colorType === 'quantitative' || ['linear','log','pow','sqrt','symlog','time','utc'].includes(scaleType);
                    if (isQuant) {
                      legendObj.orient = 'right';
                      // Compact gradient size for our layout
                      if (!('gradientLength' in legendObj)) legendObj.gradientLength = 120;
                    }
                  }
                } catch {}
                // Place symbol size legend at the bottom and horizontal
                try {
                  const size = enc.size;
                  const legendObj = ensureLegendObject(size);
                  if (legendObj && !('orient' in legendObj)) {
                    legendObj.orient = 'bottom';
                    if (!('direction' in legendObj)) legendObj.direction = 'horizontal';
                  }
                } catch {}
              };
              const setDefaultMarkColor = (unit: any, idx: number) => {
                if (!unit || typeof unit !== 'object') return;
                const enc = unit.encoding || {};
                const hasColorEnc = !!enc?.color;
                // Normalize mark to object form
                let mark = unit.mark;
                if (typeof mark === 'string') mark = { type: mark };
                if (!mark) mark = {};
                const hasExplicit = !!(mark.color || mark.fill || mark.stroke);
                if (!hasColorEnc && !hasExplicit) {
                  mark.color = CHART_PALETTE[idx % CHART_PALETTE.length];
                  unit.mark = mark;
                }
              };
              const walkUnits = (node: any, layerIdx: number = 0) => {
                if (!node || typeof node !== 'object') return;
                applyColorScale(node);
                setDefaultMarkColor(node, layerIdx);
                enforceThemeStrict(node, layerIdx);
                  adjustLegendOrientation(node);
                // Apply to layered specs as well
                if (Array.isArray(node.layer)) node.layer.forEach((ly: any, i: number) => walkUnits(ly, i));
                // Also consider faceted/repeated specs if present
                if (node.spec) walkUnits(node.spec, layerIdx);
                // Handle concatenated specs
                const concatKeys = ['hconcat', 'vconcat', 'concat'];
                for (const k of concatKeys) {
                  const arr = (node as any)[k];
                  if (Array.isArray(arr)) arr.forEach((child: any, i: number) => walkUnits(child, i));
                }
                // Handle facet and repeat
                if (node.facet && node.spec) walkUnits(node.spec, layerIdx);
                if (node.repeat && node.spec) walkUnits(node.spec, layerIdx);
              };
              walkUnits(clone);
            } catch {}
            // Normalize strokeDash anywhere in the spec (e.g., mark.strokeDash or encoding.strokeDash.value)
            try {
              const coerceDash = (val: any): number[] | undefined => {
                if (Array.isArray(val)) {
                  const arr = val.map((n) => (typeof n === 'string' ? Number(n) : n)).filter((n) => Number.isFinite(n));
                  return arr.length > 0 ? arr : undefined;
                }
                if (typeof val === 'string') {
                  const arr = val.split(/[\s,]+/).map((n) => Number(n)).filter((n) => Number.isFinite(n));
                  return arr.length > 0 ? arr : undefined;
                }
                if (typeof val === 'number' && Number.isFinite(val)) {
                  return [val];
                }
                return undefined;
              };
              const walk = (obj: any) => {
                if (!obj || typeof obj !== 'object') return;
                for (const k of Object.keys(obj)) {
                  const v = obj[k];
                  if (k === 'strokeDash') {
                    // Handle both direct arrays/strings and encoding object with { value }
                    if (v && typeof v === 'object' && !Array.isArray(v) && 'value' in v) {
                      const coerced = coerceDash((v as any).value);
                      if (coerced) (v as any).value = coerced; else delete obj[k];
                    } else {
                      const coerced = coerceDash(v);
                      if (coerced) obj[k] = coerced; else delete obj[k];
                    }
                  } else if (v && typeof v === 'object') {
                    walk(v);
                  }
                }
              };
              walk(clone);
            } catch {}

            // Fix temporal type mismatch: if data contains formatted quarter/period strings but axis is temporal, change to ordinal
            try {
              const fixTemporalMismatch = (unit: any) => {
                if (!unit || typeof unit !== 'object') return;
                const enc = unit.encoding;
                const data = unit.data?.values;
                if (!enc || !Array.isArray(data) || data.length === 0) return;
                
                // Check x and y axes
                ['x', 'y'].forEach(axis => {
                  const channel = enc[axis];
                  if (!channel || !channel.field) return;
                  
                  const fieldName = channel.field;
                  const channelType = String(channel.type || '').toLowerCase();
                  
                  // Only check if type is temporal
                  if (channelType !== 'temporal') return;
                  
                  // Check if the data values are actually temporal-parseable
                  const sampleValues = data.slice(0, 10).map((row: any) => row[fieldName]).filter((v: any) => v != null);
                  if (sampleValues.length === 0) return;
                  
                  // Test if values look like formatted quarters/periods (e.g., "Q1 2023", "2023-Q1")
                  const hasQuarterPattern = sampleValues.some((v: any) => {
                    const str = String(v);
                    return /Q[1-4]\s*\d{4}|\d{4}[-\s]Q[1-4]/i.test(str);
                  });
                  
                  // Test if values are actually parseable as dates
                  const isParseable = sampleValues.every((v: any) => {
                    try {
                      const d = new Date(v);
                      return !isNaN(d.getTime());
                    } catch {
                      return false;
                    }
                  });
                  
                  // If quarter pattern detected or not parseable, change to ordinal
                  if (hasQuarterPattern || !isParseable) {
                    console.warn(`[ChartRenderer] Fixing temporal mismatch for ${axis} field "${fieldName}" - changing to ordinal`);
                    channel.type = 'ordinal';
                  }
                });
              };
              
              const walkFixTemporal = (node: any) => {
                if (!node || typeof node !== 'object') return;
                fixTemporalMismatch(node);
                if (Array.isArray(node.layer)) node.layer.forEach(walkFixTemporal);
                if (node.spec) walkFixTemporal(node.spec);
                const concatKeys = ['hconcat', 'vconcat', 'concat'];
                for (const k of concatKeys) {
                  const arr = (node as any)[k];
                  if (Array.isArray(arr)) arr.forEach(walkFixTemporal);
                }
              };
              
              walkFixTemporal(clone);
            } catch (e) {
              console.warn('[ChartRenderer] Error fixing temporal mismatch:', e);
            }

            // Auto-colorize bar charts: if bar chart has no color encoding, add color based on x-axis
            try {
              const autoColorBars = (unit: any) => {
                if (!unit || typeof unit !== 'object') return;
                
                // Normalize mark to object
                let mark = unit.mark;
                if (typeof mark === 'string') mark = { type: mark };
                if (!mark || !mark.type) return;
                
                const markType = String(mark.type).toLowerCase();
                if (markType !== 'bar') return;
                
                const enc = unit.encoding;
                if (!enc) return;
                
                // Skip if already has color encoding
                if (enc.color) return;
                
                // Find the categorical axis (usually x for vertical bars, y for horizontal)
                const xChannel = enc.x;
                const yChannel = enc.y;
                
                let categoricalField: string | null = null;
                let categoricalFieldType: string | null = null;
                
                // Vertical bars: x is categorical/temporal, y is quantitative
                if (xChannel && yChannel) {
                  const xType = String(xChannel.type || '').toLowerCase();
                  const yType = String(yChannel.type || '').toLowerCase();
                  
                  if ((xType === 'nominal' || xType === 'ordinal' || xType === 'temporal') && yType === 'quantitative') {
                    categoricalField = xChannel.field;
                    categoricalFieldType = xType === 'temporal' ? 'temporal' : 'nominal';
                  } 
                  // Horizontal bars: y is categorical/temporal, x is quantitative
                  else if ((yType === 'nominal' || yType === 'ordinal' || yType === 'temporal') && xType === 'quantitative') {
                    categoricalField = yChannel.field;
                    categoricalFieldType = yType === 'temporal' ? 'temporal' : 'nominal';
                  }
                }
                
                // Add color encoding based on categorical/temporal field
                // Use 'nominal' type to ensure discrete colors from palette (not interpolated)
                if (categoricalField) {
                  enc.color = {
                    field: categoricalField,
                    type: 'nominal', // Force nominal to get discrete palette colors
                    scale: { range: CHART_PALETTE },
                    legend: null // Hide legend since it's redundant with axis labels
                  };
                  console.log(`[ChartRenderer] Auto-colorizing bar chart: field="${categoricalField}", type=${categoricalFieldType}→nominal`);
                }
              };
              
              const walkAutoColor = (node: any) => {
                if (!node || typeof node !== 'object') return;
                autoColorBars(node);
                if (Array.isArray(node.layer)) node.layer.forEach(walkAutoColor);
                if (node.spec) walkAutoColor(node.spec);
                const concatKeys = ['hconcat', 'vconcat', 'concat'];
                for (const k of concatKeys) {
                  const arr = (node as any)[k];
                  if (Array.isArray(arr)) arr.forEach(walkAutoColor);
                }
              };
              
              walkAutoColor(clone);
            } catch (e) {
              console.warn('[ChartRenderer] Error auto-colorizing bars:', e);
            }

            // Heuristic: infer legend item count and choose responsive legend layout
            try {
              const getLegendCountFromEnc = (enc: any, node: any): number | undefined => {
                if (!enc || !enc.color) return undefined;
                const color = enc.color;
                // Legend explicit values array
                try {
                  if (color.legend && Array.isArray(color.legend.values)) {
                    return color.legend.values.length;
                  }
                } catch {}
                // Explicit domain array on scale
                try {
                  const dom = color.scale?.domain;
                  if (Array.isArray(dom)) return dom.length;
                } catch {}
                // If inline data present, count unique values by color field
                try {
                  const fieldName = typeof color.field === 'string' ? color.field : undefined;
                  const values = node?.data?.values;
                  if (fieldName && Array.isArray(values)) {
                    const set = new Set<any>();
                    for (const row of values) {
                      if (row && Object.prototype.hasOwnProperty.call(row, fieldName)) set.add(row[fieldName]);
                      if (set.size > 1000) break; // safety cap
                    }
                    if (set.size > 0) return set.size;
                  }
                } catch {}
                return undefined;
              };

              const inferFromNode = (node: any): number | undefined => {
                if (!node || typeof node !== 'object') return undefined;
                const enc = node.encoding;
                const cnt = getLegendCountFromEnc(enc, node);
                if (typeof cnt === 'number') return cnt;
                if (Array.isArray(node.layer)) {
                  for (const ly of node.layer) {
                    const c = inferFromNode(ly);
                    if (typeof c === 'number') return c;
                  }
                }
                const concatKeys = ['hconcat', 'vconcat', 'concat'];
                for (const k of concatKeys) {
                  const arr = (node as any)[k];
                  if (Array.isArray(arr)) {
                    for (const child of arr) {
                      const c = inferFromNode(child);
                      if (typeof c === 'number') return c;
                    }
                  }
                }
                if (node.spec) return inferFromNode(node.spec);
                return undefined;
              };

              const inferredCount = inferFromNode(clone);
              const viewportW = (typeof window !== 'undefined' && window.innerWidth) ? window.innerWidth : 1024;
              const isSmallScreen = viewportW < 640;
              const threshold = 8;
              const orient = isSmallScreen ? 'bottom' : (typeof inferredCount === 'number' && inferredCount > threshold ? 'right' : 'bottom');
              const columns = orient === 'bottom' && typeof inferredCount === 'number' && inferredCount > 15 ? 2 : undefined;

              // Check if we should use custom legend (nominal/ordinal color encoding)
              const shouldUseCustomLegend = (() => {
                try {
                  const enc = clone.encoding || {};
                  const colorType = enc.color?.type?.toLowerCase();
                  return colorType === 'nominal' || colorType === 'ordinal';
                } catch {
                  return false;
                }
              })();
              
              if (shouldUseCustomLegend) {
                // Hide native Vega legend - we'll render custom one
                const hideNativeLegend = (unit: any) => {
                  if (!unit || typeof unit !== 'object') return;
                  const enc = unit.encoding || {};
                  if (enc.color) {
                    enc.color.legend = null;
                  }
                };
                const walkHideLegend = (node: any) => {
                  if (!node || typeof node !== 'object') return;
                  hideNativeLegend(node);
                  if (Array.isArray(node.layer)) node.layer.forEach(walkHideLegend);
                  if (node.spec) walkHideLegend(node.spec);
                  const concatKeys = ['hconcat', 'vconcat', 'concat'];
                  for (const k of concatKeys) {
                    const arr = (node as any)[k];
                    if (Array.isArray(arr)) arr.forEach(walkHideLegend);
                  }
                };
                walkHideLegend(clone);
                
                // Store legend orientation for custom legend
                setLegendOrientation(orient === 'right' ? 'vertical' : 'horizontal');
              } else {
                // Apply responsive legend config overrides for native Vega legend
                clone.config = clone.config || {};
                clone.config.legend = {
                  ...(clone.config.legend || {}),
                  orient: (clone.config.legend as any)?.orient ?? orient,
                  columns: (clone.config.legend as any)?.columns ?? columns,
                  labelLimit: (clone.config.legend as any)?.labelLimit ?? 320,
                } as any;
              }

              // Avoid manual bottom padding; rely on Vega autosize to compute legend space
            } catch {}
            return clone;
          } catch { return spec; }
        })();
        // Debug: log the spec we are about to render (use console.log for visibility)
        try { console.log('[ChartRenderer] rendering spec:', specToUse); } catch {}
        
        const result = await embed(innerRef.current!, specToUse as any, { actions: false, renderer: 'canvas', tooltip: true });
        if (!cancelled) {
          view = result.view;
          viewRef.current = view;
          // Notify parent component that view is ready
          if (onViewReady) {
            onViewReady(viewRef);
          }
          
          // Extract color mappings from Vega view for custom legend
          try {
            const data = specToUse?.data?.values;
            const encoding = specToUse?.encoding;
            const colorField = encoding?.color?.field;
            
            if (data && Array.isArray(data) && colorField && view) {
              // Get the color scale from the view
              const colorScale = view.scale('color');
              
              if (colorScale) {
                const colorMap = new Map<string, string>();
                
                // For each unique category value, get its color from the scale
                const uniqueValues = new Set<string>();
                data.forEach((row: any) => {
                  const rawValue = row[colorField];
                  const key = rawValue === null || rawValue === undefined || rawValue === 'null' || rawValue === 'undefined'
                    ? 'N/A'
                    : String(rawValue);
                  uniqueValues.add(key);
                });
                
                uniqueValues.forEach(value => {
                  try {
                    const color = colorScale(value);
                    if (color) {
                      colorMap.set(value, color);
                    }
                  } catch (e) {
                    // If scale lookup fails, skip this value
                  }
                });
                
                // Re-extract legend data with actual colors
                const items = extractLegendData(specToUse, data, colorMap);
                setLegendData(items);
              }
            }
          } catch (err) {
            console.error('[ChartRenderer] Failed to extract color mappings:', err);
          }
        }
        try {
          const el = innerRef.current!;
          const parent = wrapperRef.current?.parentElement as HTMLElement | null;
          const parentW = parent ? parent.getBoundingClientRect().width : undefined;
          const wrapW = wrapperRef.current?.getBoundingClientRect().width || 0;
          console.log('[ChartRenderer] mounted metrics:', { htmlLen: (el.innerHTML || '').length, w: el.clientWidth, h: el.clientHeight, wrapW, parentW });
        } catch {}
        try { onRendered && onRendered(); } catch {}
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to render chart with spec:', spec, e);
      }
    })();
    return () => { try { cancelled = true; view && view.finalize && view.finalize(); } catch {} };
  }, [spec, containerWidth]);

  React.useEffect(() => {
    async function toImage() {
      try {
        const v = viewRef.current;
        if (!v) return;
        const canvas = await v.toCanvas();
        const url = canvas.toDataURL('image/png');
        setImgUrl(url);
        setExportMode(true);
        try { if (wrapperRef.current) (wrapperRef.current as any).dataset.exportReady = 'image'; } catch {}
      } catch (e) {
        // ignore
      }
    }
    function onPrepare() { toImage(); }
    function onRestore() {
      setExportMode(false);
      setImgUrl(null);
      try { if (wrapperRef.current) delete (wrapperRef.current as any).dataset.exportReady; } catch {}
    }
    window.addEventListener('chat:export:prepare', onPrepare as any);
    window.addEventListener('chat:export:restore', onRestore as any);
    return () => {
      window.removeEventListener('chat:export:prepare', onPrepare as any);
      window.removeEventListener('chat:export:restore', onRestore as any);
    };
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%', minWidth: 0, display: 'block', overflow: 'visible' }}>
      {exportMode && imgUrl ? (
        <img src={imgUrl} alt="chart" style={{ width: '100%', height: 'auto' }} />
      ) : (
        <>
          <div ref={innerRef} style={{ width: '100%', minWidth: 0, overflow: 'visible' }} />
          {legendData && legendData.length > 0 && (
            <ChartLegend 
              items={legendData} 
              orientation={legendOrientation}
              showPercentages={true}
            />
          )}
        </>
      )}
    </div>
  );
}

// Export the download functionality for external use
export const useChartDownload = (viewRef: React.RefObject<any>) => {
  const [downloadMenuAnchor, setDownloadMenuAnchor] = React.useState<null | HTMLElement>(null);

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const downloadChart = async (format: 'png' | 'jpeg' | 'svg') => {
    try {
      const view = viewRef.current;
      if (!view) return;

      let dataUrl: string;
      let filename: string;
      let mimeType: string;

      if (format === 'svg') {
        const svgData = await view.toSVG();
        dataUrl = `data:image/svg+xml;base64,${btoa(svgData)}`;
        filename = `chart-${Date.now()}.svg`;
        mimeType = 'image/svg+xml';
      } else {
        // Use higher scale for better quality - consider device pixel ratio and add extra scaling
        const devicePixelRatio = window.devicePixelRatio || 1;
        const scaleFactor = Math.max(4, devicePixelRatio * 2); // Minimum 4x scale, or 2x device pixel ratio
        
        const canvas = await view.toCanvas(scaleFactor);
        
        // Create a new canvas with white background
        const newCanvas = document.createElement('canvas');
        newCanvas.width = canvas.width;
        newCanvas.height = canvas.height;
        const ctx = newCanvas.getContext('2d');
        if (ctx) {
          // Fill with white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);
          // Draw the chart on top
          ctx.drawImage(canvas, 0, 0);
        }
        
        // Use higher quality settings
        dataUrl = newCanvas.toDataURL(`image/${format}`, format === 'jpeg' ? 0.95 : 1.0);
        filename = `chart-${Date.now()}.${format}`;
        mimeType = `image/${format}`;
      }

      // Create download link
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download chart:', error);
    }
    handleDownloadMenuClose();
  };

  return {
    downloadMenuAnchor,
    handleDownloadMenuOpen,
    handleDownloadMenuClose,
    downloadChart
  };
};

// Download button component for external use
export const ChartDownloadButton = ({ viewRef, size = "small", variant = "default" }: { viewRef: React.RefObject<any>; size?: "small" | "medium"; variant?: "default" | "purple" }) => {
  const { downloadMenuAnchor, handleDownloadMenuOpen, handleDownloadMenuClose, downloadChart } = useChartDownload(viewRef);

  // Icon component similar to dashboard
  const IconImg = ({ src, alt }: { src: string; alt?: string }) => {
    return <img src={src} alt={alt || ''} style={{ width: 18, height: 18, display: 'block' }} />;
  };

  const isPurpleVariant = variant === "purple";

  return (
    <>
      <Tooltip title="Download chart">
        <IconButton
          size={size}
          onClick={handleDownloadMenuOpen}
          sx={{ 
            p: 0.75,
            ...(isPurpleVariant && {
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            })
          }}
        >
          <IconImg src={isPurpleVariant ? "/icons/download-white.svg" : "/icons/download.svg"} alt="download" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
          }
        }}
      >
        <MenuItem onClick={() => downloadChart('png')}>
          <ListItemIcon>
            <Image fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Download as PNG" />
        </MenuItem>
        <MenuItem onClick={() => downloadChart('jpeg')}>
          <ListItemIcon>
            <PhotoCamera fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Download as JPEG" />
        </MenuItem>
        <MenuItem onClick={() => downloadChart('svg')}>
          <ListItemIcon>
            <Code fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Download as SVG" />
        </MenuItem>
      </Menu>
    </>
  );
};